import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, LockSimple, PencilSimple, ArrowCounterClockwise, Warning } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt$ = (v) => {
  if (v == null || v === "" || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  if (n === 0) return "—";
  return `$${n.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
};
const fmt$paren = (v) => {
  if (v == null || v === 0) return "—";
  const n = Number(v);
  const s = Math.abs(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
  return n < 0 ? `($${s})` : `$${s}`;
};
const fmtPct = (v) => {
  if (v == null) return "—";
  const n = Math.abs(v) <= 1 ? v * 100 : v;
  return `${n.toFixed(0)}%`;
};
const fmtRelative = (iso) => {
  if (!iso) return null;
  try {
    const then = new Date(iso).getTime();
    const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (s < 10) return "just now";
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return null; }
};

const diffColor = (v) => (v == null || v === 0 ? "text-slate-700" : v >= 0 ? "text-emerald-600" : "text-rose-600");

const StatTile = ({ label, value, testId }) => (
  <div className="flex-1 min-w-0 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-0.5">{label}</p>
    <p className="text-lg font-black text-slate-900 truncate">{value}</p>
  </div>
);

const Row = ({ label, value, valueClass = "text-slate-900", sub = null, testId }) => (
  <div className="flex items-center justify-between px-3 py-2 odd:bg-white even:bg-slate-50" data-testid={testId}>
    <div>
      <p className="text-[11px] font-semibold text-slate-600">{label}</p>
      {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
    </div>
    <p className={`text-sm font-black tabular-nums ${valueClass}`}>{value}</p>
  </div>
);

const SectionCard = ({ title, children, testId }) => (
  <div className="rounded-xl border border-slate-200 overflow-hidden bg-white" data-testid={testId}>
    <div className="px-3 py-1.5 bg-slate-700 text-white text-[11px] font-black tracking-widest uppercase text-center" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
      {title}
    </div>
    <div>{children}</div>
  </div>
);

/**
 * DailyKpiEditor — inline daily inputs that live inside the Today
 * Eyes-on-the-Biz card. Holds **Today's targets** (Plan $, Comp Goal $)
 * + daily KPIs (ADS, UPT). AM-entered once, lock for the day. Persisted
 * via `PUT /api/biz/day` keyed on (store_id, date) so admin mirror +
 * multi-tenant scoping already work. Locked-state shows the saved
 * values plus an "Edit KPIs" link so a manager/admin has to
 * intentionally re-open the inputs. Printed Zone Sheet PDF reads these
 * via `/api/biz/{date}` and `/api/biz/wtd/{date}`.
 *
 * Plan $ / Comp Goal $ used to live inside the 2pm checkpoint panel
 * (CheckpointEntry → Today's targets card) which was locked behind the
 * 2pm clock unlock. Moved here 2026-06-13 so morning prints of the
 * Zone Sheet capture Today's Targets even when run before 2pm.
 */
export const DailyKpiEditor = ({ date, biz, actorId, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [planSales, setPlanSales] = useState("");
  const [compGoalSales, setCompGoalSales] = useState("");
  const [ads, setAds] = useState("");
  const [upt, setUpt] = useState("");
  const [saving, setSaving] = useState(false);
  // Reset-day flow (P2-e, user ask 2026-02): a manager can wipe today's
  // Plan / Comp / ADS / UPT if they entered them wrong. Two-step
  // confirmation via AlertDialog so a stray tap can't nuke the day —
  // user explicitly asked "add an extra stop in case the manager
  // click reset day". `resetOpen` opens the dialog on first tap;
  // `resetting` disables the confirm button while the DELETE fires.
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Seed the local inputs whenever the underlying biz doc changes (date
  // switch, parent refreshKey bump, post-save refetch). Without this the
  // editor would always open empty even after a value was saved.
  useEffect(() => {
    setPlanSales(biz?.plan_sales ?? "");
    setCompGoalSales(biz?.comp_goal_sales ?? "");
    setAds(biz?.ads ?? "");
    setUpt(biz?.upt ?? "");
  }, [biz?.plan_sales, biz?.comp_goal_sales, biz?.ads, biz?.upt, date]);

  // Considered "saved" if ANY of the four fields has a value persisted —
  // managers don't have to enter all four at once.
  const hasSaved = biz?.plan_sales != null || biz?.comp_goal_sales != null || biz?.ads != null || biz?.upt != null;
  const showEditor = editing || !hasSaved;

  const save = async () => {
    if (planSales === "" && compGoalSales === "" && ads === "" && upt === "") return;
    setSaving(true);
    try {
      await axios.put(`${API}/biz/day`, {
        date,
        plan_sales: planSales === "" ? null : Number(planSales),
        comp_goal_sales: compGoalSales === "" ? null : Number(compGoalSales),
        ads: ads === "" ? null : Number(ads),
        upt: upt === "" ? null : Number(upt),
        actor_id: actorId,
      });
      toast.success("Daily KPIs saved");
      setEditing(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const resetDay = async () => {
    // Fires the DELETE /api/biz/day/{date} endpoint. Server preserves
    // checkpoints + snapshots — only the top-level Plan/Comp/ADS/UPT
    // are cleared. Two-step guard means we're already past the
    // confirm-in-dialog stage by the time this runs.
    setResetting(true);
    try {
      await axios.delete(`${API}/biz/day/${date}`, {
        params: { actor_id: actorId },
      });
      toast.success("Day reset — Plan, Comp Goal, ADS, and UPT cleared");
      // Local input state also cleared so the editor doesn't re-show
      // the old values after the dialog closes.
      setPlanSales("");
      setCompGoalSales("");
      setAds("");
      setUpt("");
      setEditing(false);
      setResetOpen(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reset day");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="border-t border-slate-200 p-3" data-testid="biz-daily-kpis">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Daily KPIs</p>
        {hasSaved && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-700 hover:text-blue-900 underline"
            data-testid="biz-daily-kpis-edit"
          >
            <PencilSimple weight="bold" className="w-3 h-3" /> Edit KPIs
          </button>
        )}
      </div>
      {showEditor ? (
        <>
          {/* Today's targets — Plan $ + Comp Goal $. Used to live in the
              2pm checkpoint card (locked behind the 2pm clock); moved
              here so a manager can enter them in the AM and the
              printed Zone Sheet pulls them even on a pre-2pm print. */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Plan $</label>
              <Input type="number" step="0.01" value={planSales} onChange={(e) => setPlanSales(e.target.value)} placeholder="3815" data-testid="biz-plan-sales" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Comp Goal $</label>
              <Input type="number" step="0.01" value={compGoalSales} onChange={(e) => setCompGoalSales(e.target.value)} placeholder="8614" data-testid="biz-comp-goal" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div>
              <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">ADS $</label>
              <Input type="number" step="0.01" value={ads} onChange={(e) => setAds(e.target.value)} placeholder="11.50" data-testid="biz-daily-ads-input" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">UPT</label>
              <Input type="number" step="0.01" value={upt} onChange={(e) => setUpt(e.target.value)} placeholder="2.4" data-testid="biz-daily-upt-input" />
            </div>
          </div>
          <div className="flex gap-2 mt-2">
            <Button onClick={save} disabled={saving} className="flex-1 h-9 fb-btn-primary" data-testid="biz-daily-kpis-save">
              {saving ? "Saving…" : "Save daily KPIs"}
            </Button>
            {hasSaved && (
              <Button
                variant="outline"
                onClick={() => {
                  setEditing(false);
                  setPlanSales(biz?.plan_sales ?? "");
                  setCompGoalSales(biz?.comp_goal_sales ?? "");
                  setAds(biz?.ads ?? "");
                  setUpt(biz?.upt ?? "");
                }}
                disabled={saving}
                className="h-9"
                data-testid="biz-daily-kpis-cancel"
              >Cancel</Button>
            )}
          </div>
          {/* Reset Day — appears only when there are values to reset.
              Two-step confirmation so a stray tap doesn't nuke the day
              (user explicit ask 2026-02). Sits BELOW the Save/Cancel
              row and uses rose-tinted "danger" styling so it never
              feels like a primary action. */}
          {hasSaved && (
            <div className="mt-2 pt-2 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setResetOpen(true)}
                disabled={saving || resetting}
                className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-rose-600 hover:text-rose-800 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="biz-daily-kpis-reset"
              >
                <ArrowCounterClockwise weight="bold" className="w-3.5 h-3.5" />
                Reset day (clear Plan, Comp, ADS, UPT)
              </button>
            </div>
          )}
          <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
            <AlertDialogContent
              data-testid="biz-reset-day-dialog"
              // Force the user to make an EXPLICIT choice — Escape and
              // outside-click both blocked so the "extra stop" the user
              // asked for really is a stop. Only the Cancel and Confirm
              // buttons dismiss the dialog. (Iter 49 reviewer flagged
              // the default Escape behavior as a minor spec deviation.)
              onEscapeKeyDown={(e) => e.preventDefault()}
            >
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <Warning weight="fill" className="w-5 h-5 text-rose-500" />
                  Reset today&apos;s Plan &amp; KPIs?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  This clears <strong>Plan $, Comp Goal $, ADS, and UPT</strong> for{" "}
                  <strong>{date}</strong>. Your checkpoint captures (2PM / 5PM /
                  8PM) and email tally are <em>NOT</em> touched — only the day
                  targets you enter in the morning.
                  <span className="block mt-2 text-rose-600 font-semibold">
                    This action can&apos;t be undone from the app — you&apos;d have to re-enter
                    the correct numbers.
                  </span>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  disabled={resetting}
                  data-testid="biz-reset-day-cancel"
                >Nevermind, keep them</AlertDialogCancel>
                <AlertDialogAction
                  onClick={resetDay}
                  disabled={resetting}
                  className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-500"
                  data-testid="biz-reset-day-confirm"
                >
                  {resetting ? "Resetting…" : "Yes, reset the day"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      ) : (
        <div className="flex items-center justify-between rounded-md bg-slate-50 border border-slate-200 px-3 py-2" data-testid="biz-daily-kpis-locked">
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-[12px] font-bold text-slate-800">
            <span data-testid="biz-daily-plan-display">
              Plan $: {biz?.plan_sales != null ? `$${Number(biz.plan_sales).toLocaleString()}` : <em className="text-slate-400 font-normal">not set</em>}
            </span>
            <span data-testid="biz-daily-comp-goal-display">
              Comp Goal $: {biz?.comp_goal_sales != null ? `$${Number(biz.comp_goal_sales).toLocaleString()}` : <em className="text-slate-400 font-normal">not set</em>}
            </span>
            <span data-testid="biz-daily-ads-display">
              ADS: {biz?.ads != null ? `$${Number(biz.ads).toLocaleString("en-US", { maximumFractionDigits: 2 })}` : <em className="text-slate-400 font-normal">not set</em>}
            </span>
            <span data-testid="biz-daily-upt-display">
              UPT: {biz?.upt != null ? Number(biz.upt).toLocaleString("en-US", { maximumFractionDigits: 2 }) : <em className="text-slate-400 font-normal">not set</em>}
            </span>
          </div>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
            <LockSimple weight="bold" className="w-3 h-3" /> Locked
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * Eyes on the Biz — live preview. Redesigned from a single wide table into
 * three stacked card groups so nothing wraps vertically on mobile/tablet
 * viewports. Structure mirrors the paper sheet's content:
 *   1. TODAY — Plan / Comp Goal / # of Orders as horizontal stat tiles.
 *   2. TODAY KPIs — compact 2pm/5pm/8pm × SALES/BOPIS grid.
 *   3. WEEK TO DATE — two side-by-side cards (WTD Sales Plan + WTD Comp
 *      Sales), each with its own stacked rows.
 * NPS and Greet were removed 2026-05-13 — the team stopped tracking them.
 */
export default function EyesOnBizPreview({ date, refreshKey = 0, hideHeader = false, showWTD = true, showToday = true, user = null }) {
  const [biz, setBiz] = useState(null);
  const [wtd, setWtd] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [b, w] = await Promise.all([
        axios.get(`${API}/biz/${date}`),
        axios.get(`${API}/biz/wtd/${date}`),
      ]);
      setBiz(b.data || {});
      setWtd(w.data || {});
    } catch { /* non-critical */ }
  }, [date]);

  // Re-fetch whenever the date changes OR the parent bumps refreshKey
  // (after any Eyes-on-the-Biz dialog save).
  useEffect(() => { refresh(); }, [refresh, refreshKey]);

  // Tick the "Updated Xs ago" label every 30s so it stays fresh without
  // needing to refetch the whole biz doc. Cheap — just forces a render.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forceTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  if (!biz || !wtd) return null;

  // Manager-only gate for WTD financial figures. WowCrew users see the
  // Today checkpoint metrics (those drive their email-capture goals)
  // but never the WTD Sales Plan / Comp / LY figures — confidential
  // store performance data. (User privacy ask 2026-06-12 "WTD
  // financial information should be manager-only".)
  const isManagerLike = !!(user?.is_admin || user?.is_manager || user?.is_district_manager || user?._real_is_admin);
  const renderWTD = showWTD && isManagerLike;

  const cps = biz.checkpoints || {};
  const kpi = (slot, key) => {
    const v = (cps[slot] || {})[key];
    if (v == null || v === "") return "—";
    if (key === "sales") return fmt$(v);
    return v;
  };

  return (
    <div className={hideHeader ? "space-y-3" : "dashboard-card space-y-3"} data-testid="eyes-on-biz-preview">
      {/* Header — hidden when embedded inside the Zone Sheet's
          collapsible Eyes-on-the-Biz section to avoid duplication.
          On Manager Dashboard (deprecated context) it still rendered;
          since 2026-02-15 the tile was removed from Manager Dashboard
          so this header now only appears if some future caller
          mounts the preview standalone. */}
      {!hideHeader && (
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#E2E8F0" }}>
          <Eye weight="bold" className="w-4 h-4 text-slate-700" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold tracking-tight" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
            Eyes on the Biz
          </h3>
          {biz.updated_at ? (
            <p className="text-[11px] text-slate-500" data-testid="biz-last-updated">
              Updated {fmtRelative(biz.updated_at)} by {biz.updated_by_name || "Manager"}
            </p>
          ) : (
            <p className="text-[11px] text-slate-500">Live preview of the EOD email. Updates as each checkpoint is saved.</p>
          )}
        </div>
      </div>
      )}

      {/* === TODAY === */}
      {showToday && (
      <SectionCard title="Today" testId="biz-today-card">
        {/* Top-line cards. "Sales $" is the live running total of the Sales $
            entered at 2pm / 5pm / 8pm below — same number that lands as
            `actual_sales` in the DB and the EOD email. */}
        <div className="flex gap-2 p-3">
          <StatTile label="Sales $" value={fmt$(biz.actual_sales)} testId="biz-today-sales" />
          <StatTile label="Plan $" value={fmt$(biz.plan_sales)} testId="biz-today-plan" />
          <StatTile label="Comp Goal $" value={fmt$(biz.comp_goal_sales)} testId="biz-today-comp-goal" />
        </div>

        {/* Checkpoint KPIs grid */}
        <table className="w-full text-sm border-collapse border-t border-slate-200" data-testid="biz-today-kpis">
          <thead>
            <tr className="bg-slate-100">
              <th className="px-2 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-left"></th>
              <th className="px-2 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center">Sales</th>
              <th className="px-2 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center">BOPIS</th>
            </tr>
          </thead>
          <tbody>
            {["2pm", "5pm", "8pm"].map((slot) => (
              <tr key={slot} className="border-t border-slate-100">
                <td className="px-2 py-1.5 text-[11px] font-bold text-slate-600 bg-slate-50">{(biz.checkpoint_labels?.[slot] || slot).toLowerCase()}</td>
                <td className="px-2 py-1.5 text-center text-[13px] tabular-nums">{kpi(slot, "sales")}</td>
                <td className="px-2 py-1.5 text-center text-[13px] tabular-nums">{kpi(slot, "bopis")}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Daily KPIs — ADS / UPT. Manager enters once in the AM and they
            lock for the rest of the business day; the printed Zone Sheet
            PDF pulls these exact values via /api/biz/wtd/{date}. Edit
            requires an intentional Edit-KPIs click. Manager-only gate so
            WowCrew users don't see them. (User ask 2026-06-13 "ADS and
            UPT need to be updated daily, not weekly only".) */}
        {isManagerLike && (
          <DailyKpiEditor
            date={date}
            biz={biz}
            actorId={user?.crew_id || ""}
            onSaved={refresh}
          />
        )}
      </SectionCard>
      )}

      {/* === WEEK TO DATE — two cards === */}
      {renderWTD && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="biz-wtd-block">
        <SectionCard title="WTD Sales Plan" testId="biz-wtd-sales-plan">
          <Row label="Actual $" value={fmt$(wtd.actual_sales)} testId="wtd-sp-actual" />
          <Row label="Plan $ (week)" value={fmt$(wtd.plan_sales)} testId="wtd-sp-plan" />
          <Row label="Plan to Date" value={fmt$(wtd.plan_to_date)} testId="wtd-sp-plan-to-date" />
          <Row label="vs. +/- PLAN $" value={fmt$paren(wtd.diff_plan_dollars)} valueClass={diffColor(wtd.diff_plan_dollars)} testId="wtd-sp-diff-dollars" />
          <Row label="vs. +/- PLAN %" value={fmtPct(wtd.diff_plan_pct)} valueClass={diffColor(wtd.diff_plan_pct)} testId="wtd-sp-diff-pct" />
          {/* Comp % — Flash Sales headline metric = WTD Actual vs WTD LY
              variance. Same math as +/- LY % below but surfaced up here
              alongside +/- Plan % so both corporate KPIs are visible at
              a glance. (User ask 2026-02-27.) */}
          <Row label="Comp %" value={fmtPct(wtd.diff_ly_pct)} valueClass={diffColor(wtd.diff_ly_pct)} testId="wtd-sp-comp-pct" />
          {/* Plan-to-date source nudge (2026-05-03). If the backend had to
              pro-rate from the weekly plan because a 2PM daily plan entry
              was missed, show an amber reminder so the manager knows the
              diff is estimated (not wrong — just derived). */}
          {wtd.plan_to_date_source === "weekly_prorate" && (
            <p
              className="mt-2 text-[11px] leading-snug rounded-md bg-amber-50 border border-amber-200 text-amber-800 px-2 py-1.5"
              data-testid="wtd-sp-plan-source-nudge"
            >
              <span className="font-bold">Plan to Date is pro-rated</span> from the weekly plan — {wtd.daily_plan_days_filled || 0}/{wtd.days_elapsed || 0} days had daily Plan $ filled. Enter daily plan at 2PM for exact diff.
            </p>
          )}
          {wtd.plan_to_date_source === "missing" && (
            <p
              className="mt-2 text-[11px] leading-snug rounded-md bg-rose-50 border border-rose-200 text-rose-800 px-2 py-1.5"
              data-testid="wtd-sp-plan-source-nudge"
            >
              <span className="font-bold">No Plan $ entered</span> for this week. Add the weekly plan under Weekly LY + Plan, or enter daily Plan $ at 2PM to unlock +/- diffs.
            </p>
          )}
        </SectionCard>

        <SectionCard title="WTD Comp Sales" testId="biz-wtd-comp-sales">
          <Row label="Actual $" value={fmt$(wtd.actual_comp_sales)} testId="wtd-cs-actual" />
          <Row label="LY Sales $ (week)" value={fmt$(wtd.ly_sales)} testId="wtd-cs-ly" />
          <Row label="LY to Date" value={fmt$(wtd.ly_to_date)} testId="wtd-cs-ly-to-date" />
          <Row label="vs. +/- LY COMP $" value={fmt$paren(wtd.diff_ly_dollars)} valueClass={diffColor(wtd.diff_ly_dollars)} testId="wtd-cs-diff-dollars" />
          <Row label="vs. +/- LY COMP %" value={fmtPct(wtd.diff_ly_pct)} valueClass={diffColor(wtd.diff_ly_pct)} testId="wtd-cs-diff-pct" />
        </SectionCard>
      </div>
      )}
    </div>
  );
}
